/**
 * Brand Logo Component
 *
 * Use this component instead of hardcoding logo paths.
 * Logo paths are controlled from src/config/branding.ts
 */

import type { CSSProperties } from "react";
import { branding } from "../config/branding";
import { useOptionalOfferDashboard } from "../context/OfferDashboardContext";

interface BrandLogoProps {
  /** Logo variant: 'primary' for main logo, 'wordmark' for text logo */
  variant?: "primary" | "wordmark";
  /** Additional CSS classes */
  className?: string;
  /** Height (CSS value or number for pixels) */
  height?: number | string;
  /** Width (CSS value or number for pixels) */
  width?: number | string;
}

export function BrandLogo({
  variant = "primary",
  className = "",
  height,
  width,
}: BrandLogoProps) {
  const offerDashboard = useOptionalOfferDashboard();
  const embeddedBranding = offerDashboard?.isEmbeddedMode && offerDashboard.activeDashboard
    ? offerDashboard.activeDashboard.branding
    : null;
  const themeMode = embeddedBranding ? offerDashboard?.themeMode : undefined;
  const modeLogo = themeMode && embeddedBranding?.modeLogos?.[themeMode]
    ? embeddedBranding.modeLogos[themeMode]
    : null;
  const src = variant === "primary"
    ? modeLogo || (embeddedBranding ? embeddedBranding.logos.primary : branding.logos.primary)
    : (embeddedBranding ? embeddedBranding.logos.wordmark : branding.logos.wordmark);

  const alt = `${embeddedBranding?.companyName || branding.company.displayName || branding.company.name} Logo`;

  const style: CSSProperties = {};
  if (height !== undefined) {
    style.height = typeof height === "number" ? `${height}px` : height;
  }
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  if (themeMode && embeddedBranding?.invertLogoModes?.includes(themeMode)) {
    style.filter = "brightness(0) invert(1)";
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={Object.keys(style).length > 0 ? style : undefined}
    />
  );
}

/** Company name from branding config */
export const companyName = branding.company.displayName || branding.company.name;

/** Company tagline from branding config */
export const companyTagline = branding.company.tagline;

/** Full branding config export for advanced usage */
export { branding } from "../config/branding";
